import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Paper,
  useTheme,
  CircularProgress,
  Tooltip,
  Avatar,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { companyProfileService } from '../../services/companyProfileService';

interface CompanyLogoUploaderProps {
  companyId: number;
  value?: string;
  onChange: (logoUrl: string | null) => void;
  disabled?: boolean;
  error?: string;
}

export const CompanyLogoUploader: React.FC<CompanyLogoUploaderProps> = ({
  companyId,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = useCallback(
    async (file: File) => {
      if (!file || disabled) return;

      // ファイル検証
      if (!file.type.startsWith('image/')) {
        setUploadError('画像ファイルのみアップロード可能です');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setUploadError('ファイルサイズは5MB以下にしてください');
        return;
      }

      setUploading(true);
      setUploadError(null);

      try {
        const response = await companyProfileService.uploadCompanyLogo(
          companyId,
          file
        );
        onChange(response.data.logo_url);
      } catch (error: any) {
        console.error('ロゴアップロードエラー:', error);
        const errorMessage =
          error.response?.data?.error?.message ||
          'ロゴのアップロードに失敗しました';
        setUploadError(errorMessage);
      } finally {
        setUploading(false);
      }
    },
    [companyId, onChange, disabled]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
    // ファイル入力をリセット
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragActive(false);

      if (disabled) return;

      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileChange(file);
      }
    },
    [handleFileChange, disabled]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleRemove = () => {
    onChange(null);
    setUploadError(null);
  };

  const displayError = uploadError || error;

  return (
    <Box>
      {/* 現在のロゴ表示 */}
      {value && (
        <Box sx={{ mb: 2 }}>
          <Card sx={{ maxWidth: 300 }}>
            <CardContent sx={{ textAlign: 'center', pb: 1 }}>
              <Avatar
                src={value}
                alt="企業ロゴ"
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: 'common.white',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <BusinessIcon sx={{ fontSize: 40, color: 'grey.500' }} />
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                現在のロゴ
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
              <Tooltip title="ロゴを削除">
                <IconButton
                  size="small"
                  onClick={handleRemove}
                  disabled={disabled}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </CardActions>
          </Card>
        </Box>
      )}

      {/* アップロードエリア */}
      <Paper
        sx={{
          border: 2,
          borderColor: dragActive
            ? theme.palette.primary.main
            : displayError
              ? theme.palette.error.main
              : theme.palette.divider,
          borderStyle: 'dashed',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          backgroundColor: dragActive
            ? theme.palette.action.hover
            : disabled
              ? theme.palette.action.disabledBackground
              : 'transparent',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          disabled={disabled || uploading}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />

        {uploading ? (
          <Box sx={{ py: 2 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              ロゴをアップロード中...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: disabled
                  ? theme.palette.text.disabled
                  : dragActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                mb: 1,
              }}
            />
            <Typography
              variant="h6"
              color={disabled ? 'text.disabled' : 'text.primary'}
              gutterBottom
            >
              {value ? 'ロゴを変更' : 'ロゴをアップロード'}
            </Typography>
            <Typography
              variant="body2"
              color={disabled ? 'text.disabled' : 'text.secondary'}
              sx={{ mb: 2 }}
            >
              ファイルをドロップするか、クリックして選択してください
            </Typography>
            <Typography variant="caption" color="text.disabled">
              対応形式: JPG, PNG, GIF, WebP（最大5MB）
            </Typography>
          </Box>
        )}
      </Paper>

      {/* エラー表示 */}
      {displayError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">{displayError}</Typography>
        </Alert>
      )}

      {/* ヘルパーテキスト（URL指定と同じスタイル） */}
      {!displayError && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 1,
            display: 'block',
            fontSize: '0.75rem',
            lineHeight: 1.66,
          }}
        >
          学生向けプロフィールやスポンサー表示で利用される企業ロゴをアップロードしてください
        </Typography>
      )}
    </Box>
  );
};
